import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileItem, FileLikeObject, FileUploader, FileUploaderOptions, ParsedResponseHeaders } from 'ng2-file-upload';
import { Alert } from 'src/app/interfaces/alert';
import { environment } from 'src/environments/environment';
import { Pet } from 'src/app/interfaces/pet';
import { AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { SafeUrl } from '@angular/platform-browser';

export enum RepositoryType {
  Azure = 1,
  Cloudinary
}

export interface ImageContent {
  fileName: string;
  filePath: string;
  fileType: number;
  id: number;
  repository: RepositoryType;
  typeDescription: string;
}

@Component({
  selector: 'app-image-uploader-modal',
  templateUrl: './image-uploader-modal.component.html',
  styleUrls: ['./image-uploader-modal.component.scss']
})
export class ImageUploaderModalComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() currentImageId: string;
  @Input() currentImageUrl: string;
  @Input() pet?: Pet;
  @Input() disablePetDefault?: boolean;
  @Input() filters?: string[];
  @Input() maxFileSizeInMB?: number;

  imageChangedEvent: Event | null = null;
  croppedImage: SafeUrl  = '';
  resizeState: boolean = false;
  isResized: boolean = false;
  originalFileItem: FileItem | null = null;
  uploader: FileUploader;
  loading = false;
  loadingProgess = 0;
  alert: Alert = null;
  allowedMimeType = [
    'image/png',
    'image/gif',
    'image/jpeg',
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private authManagerService: AuthManagerService,
  ) {

  }

  upload(): void {
    this.closeAlert();
    this.loading = true;
    this.loadingProgess = 0;
    this.uploader.uploadAll();
  }

  closeAlert() {
    this.alert = null;
  }

  private getUploadUrl(): string {
    if (this.pet) {
      return `${environment.apiUrl}/api/users/me/pets/${this.pet.id}/image/upload${!this.disablePetDefault ? '/default' : ''}`;
    }
    return `${environment.apiUrl}/api/content/upload/${RepositoryType.Cloudinary}`;
  }

  private getUploaderOptionsHeaders(): any {
    const headers = [
      {
        name: 'X-Requested-With',
        value: 'XMLHttpRequest'
      },
      {
        name: 'Authorization',
        value: `Bearer ${this.authManagerService.token}`
      }
    ];
    if (this.filters) {
      const headerFilters = [];
      this.filters.forEach(filter => {
        const updatedFilter = `'${filter.replace(/[^0-9A-Za-z]/g, '')}'`;
        headerFilters.push(updatedFilter);
      });
      headers.push({
        name: 'filters',
        value: headerFilters.join(' ')
      });
    }
    return headers;
  }

  fileChangeEvent(event: Event): void {
    this.imageChangedEvent = event;
  }
  
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.objectUrl;
    this.isResized = true;

    const file = new File([event.blob], 'cropped-image.png', { type: 'image/png' });
    const fileItem = new FileItem(this.uploader, file, this.uploader.options);
    Object.defineProperty(fileItem, 'file', { value: file });

    this.uploader.clearQueue();
    this.uploader.queue.push(fileItem);
  }

  toggleResizeState() {
    this.resizeState = !this.resizeState;
  }

  cancel(): void {
    if (this.isResized && this.originalFileItem && !this.loading) {
      this.uploader.clearQueue();
      this.uploader.queue.push(this.originalFileItem);
      this.croppedImage = '';
      this.isResized = false;
    } else {
      this.activeModal.dismiss();
    }
  }

  ngOnInit(): void {
    // Set default maxFileSize in Megabytes
    if (!this.maxFileSizeInMB) {
      this.maxFileSizeInMB = 20;
    }

    const uploadUrl = this.getUploadUrl();

    const uploaderOptions: FileUploaderOptions = {
      url: uploadUrl,
      isHTML5: true,
      queueLimit: 2,
      headers: this.getUploaderOptionsHeaders(),
      allowedMimeType: this.allowedMimeType,
      maxFileSize: this.maxFileSizeInMB  * 1048576,
    };
    this.uploader = new FileUploader(uploaderOptions);

    this.uploader.onAfterAddingFile = (fileItem: any): any => {
      this.closeAlert();
      fileItem.withCredentials = false;
      // Making sure the queue contains only one file item
      if (this.uploader.queue.length === 2) {
        this.uploader.removeFromQueue(this.uploader.queue[0]);
      }

      this.originalFileItem = fileItem;
      this.isResized = false; 
    };

    this.uploader.onWhenAddingFileFailed = (item: FileLikeObject, filter: any, options: any): any => {
      if (filter.name === 'mimeType') {
        this.alert = {
          type: 'danger',
          message: `File type not supported. Please add a file of type: ${this.allowedMimeType.join(', ')}.`
        };
      } else if (filter.name === 'fileSize') {
        this.alert = {
          type: 'danger',
          message: `This image can't be uploaded because it exceeds the maximum file size of <strong>${this.maxFileSizeInMB}MB</strong>.`
        };
      } else {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.'
        };
      }
    };

    this.uploader.onProgressItem = (fileItem: any, progress: any) => {
      this.loadingProgess = progress;
    };

    this.uploader.onCompleteItem = (fileItem: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      this.loading = false;
      this.loadingProgess = 0;
      if (status === 200 || status === 201) {
        const res: ImageContent = JSON.parse(response);
        this.activeModal.close(res);
      } else {
        // When the upload fails, the library set the fileItem as uploaded when it's not.
        fileItem.isUploaded = false;
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.'
        };
      }
    };

    this.uploader.onBuildItemForm = (fileItem: any, form: FormData): any => {
      // Add Cloudinary's unsigned upload preset to the upload form
      // form.append('upload_preset', this.cloudinary.config().upload_preset);
    };
  }

  ngOnDestroy(): void {
    // Make sure to cancel any uploads if the user close the modal
    this.uploader?.cancelAll();
    this.uploader?.clearQueue();
  }

}
